import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { AppContext } from '../App';
import { useFilters } from '../context/FilterContext';
import { useCachedData } from '../hooks/useCachedData';
import { TrendingUp, DollarSign, Users, Clock, ChevronRight, LayoutDashboard, TrendingDown, Calendar, UserCheck, Briefcase, CreditCard, Building2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import EmployeeDrillDown from '../components/EmployeeDrillDown';
import OvertimeTrendline from '../components/OvertimeTrendline';
import SeasonalAnomalies from '../components/SeasonalAnomalies';
import SupervisorAnalysis from '../components/SupervisorAnalysis';
import PayTypeBreakdown from '../components/PayTypeBreakdown';
import JobTitleBreakdown from '../components/JobTitleBreakdown';
import OrgStructure from '../components/OrgStructure';
import { PAY_TYPE_COLORS, DEFAULT_MULTIPLIERS, getPayTypeColor } from '../utils/payTypeColors';

// Smart number formatting - shows M for millions, k for thousands, or raw for small numbers
const formatNumber = (num, prefix = '') => {
  if (num === null || num === undefined) return prefix + '0';
  const absNum = Math.abs(num);
  if (absNum >= 1000000) {
    return prefix + (num / 1000000).toFixed(2) + 'M';
  } else if (absNum >= 100000) {
    return prefix + (num / 1000).toFixed(0) + 'k';
  } else if (absNum >= 10000) {
    return prefix + (num / 1000).toFixed(1) + 'k';
  } else if (absNum >= 1000) {
    // Show 1.9k format for 1000-9999
    return prefix + (num / 1000).toFixed(1) + 'k';
  } else {
    return prefix + num.toLocaleString();
  }
};

// Format currency with smart formatting
const formatCurrency = (num) => formatNumber(num, '$');

// Format hours with smart formatting
const formatHours = (num) => formatNumber(num);

// Skeleton components for better perceived performance
const KPISkeleton = () => (
  <div className="kpi-card animate-pulse">
    <div className="flex justify-between items-start mb-3">
      <div className="w-10 h-10 bg-slate-200 rounded-lg"></div>
    </div>
    <div className="h-8 w-20 bg-slate-200 rounded mb-2"></div>
    <div className="h-4 w-32 bg-slate-100 rounded"></div>
  </div>
);

const ChartSkeleton = ({ height = 350 }) => (
  <div className="kpi-card animate-pulse">
    <div className="h-6 w-48 bg-slate-200 rounded mb-4"></div>
    <div className="h-4 w-64 bg-slate-100 rounded mb-4"></div>
    <div className={`bg-slate-100 rounded-lg`} style={{ height: `${height}px` }}></div>
  </div>
);

const TableSkeleton = () => (
  <div className="kpi-card animate-pulse">
    <div className="h-6 w-48 bg-slate-200 rounded mb-4"></div>
    <div className="h-4 w-64 bg-slate-100 rounded mb-4"></div>
    <div className="space-y-3">
      {[...Array(5)].map((_, i) => (
        <div key={`tbl-skel-${i}`} className="flex justify-between items-center py-3 border-b border-slate-100">
          <div className="h-4 w-32 bg-slate-100 rounded"></div>
          <div className="h-4 w-20 bg-slate-100 rounded"></div>
          <div className="h-4 w-20 bg-slate-100 rounded"></div>
          <div className="h-4 w-16 bg-slate-100 rounded"></div>
        </div>
      ))}
    </div>
  </div>
);

const Dashboard = () => {
  const { API, premiumPayTypes } = React.useContext(AppContext);
  const { filters } = useFilters();
  const [exporting, setExporting] = useState(false);
  const [drillDownOpen, setDrillDownOpen] = useState(false);
  const [selectedDimension, setSelectedDimension] = useState({ type: null, value: null, payType: null, location: null, department: null });
  const [chatMessages, setChatMessages] = useState([]);
  
  // Load chat messages from sessionStorage for export
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem('overtimeiq_chat_messages');
      if (stored) {
        setChatMessages(JSON.parse(stored));
      }
    } catch (e) {
      // Non-critical: chat-export hydration. Only surface in dev to avoid noisy
      // production console without sacrificing debuggability.
      if (process.env.NODE_ENV === 'development') {
        console.warn('Could not hydrate chat messages from sessionStorage:', e);
      }
    }
  }, []);
  const [activeTab, setActiveTab] = useState('overview');

  // Handle row click for drill-down, optionally filtered by pay type
  const handleRowClick = (dimension, payType = null, filterType = 'department') => {
    setSelectedDimension({ type: filterType, value: dimension, payType, location: null, department: null });
    setDrillDownOpen(true);
  };

  // Fetch dashboard data with DB-freshness-aware cache
  const fetchDashboard = useCallback(async () => {
    const params = {
      group_by: 'department',
      start_date: filters?.start_date || undefined,
      end_date: filters?.end_date || undefined,
      department: filters?.department || undefined,
      location: filters?.location || undefined,
      manager: filters?.manager || undefined,
      pay_types: filters?.pay_types && filters.pay_types.length > 0 ? filters.pay_types : undefined
    };
    const response = await axios.post(`${API}/overtime/analysis`, params);
    return response.data;
  }, [API, filters?.start_date, filters?.end_date, filters?.department, filters?.location, filters?.manager, filters?.pay_types]);

  const { data, loading } = useCachedData(
    'dashboard',
    fetchDashboard,
    [filters?.start_date, filters?.end_date, filters?.department, filters?.location, filters?.manager, JSON.stringify(filters?.pay_types)]
  );

  const handleExport = async (format, options = {}) => {
    const { includeAllData = false, includeChat = false, chatMessages = null } = options;
    
    setExporting(true);
    try {
      const endpoint = `${API}/export/${format}`;
      const response = await axios.post(
        endpoint,
        {
          title: 'OvertimeIQ Analysis Report',
          include_charts: true,
          include_all_data: includeAllData,
          include_insights: true,
          include_recommendations: true,
          include_chat: includeChat,
          chat_messages: chatMessages,
          filters: {
            start_date: filters.start_date,
            end_date: filters.end_date,
            department: filters.department,
            location: filters.location,
            manager: filters.manager,
            pay_types: filters.pay_types && filters.pay_types.length > 0 ? filters.pay_types : null
          }
        },
        { responseType: 'blob' }
      );

      const extensions = { 
        pdf: 'pdf', 
        word: 'docx', 
        powerpoint: 'pptx',
        'executive-summary': 'pdf'
      };
      const mimeTypes = {
        pdf: 'application/pdf',
        word: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        powerpoint: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'executive-summary': 'application/pdf'
      };

      const filename = format === 'executive-summary' 
        ? `executive_summary_${new Date().toISOString().split('T')[0]}.pdf`
        : `overtime_report.${extensions[format]}`;

      const url = window.URL.createObjectURL(new Blob([response.data], { type: mimeTypes[format] }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      const successMsg = format === 'executive-summary' 
        ? 'Executive Summary exported!'
        : `${format.toUpperCase()} report exported successfully!`;
      toast.success(successMsg);
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  const handleDrillDown = (filter) => {
    // Support hierarchical drill-down from Org Structure
    // filter can contain: type, value, location (optional), department (optional)
    setSelectedDimension({
      type: filter.type,
      value: filter.value,
      payType: null,
      location: filter.location || null,  // Parent location context
      department: filter.department || null  // Parent department context
    });
    setDrillDownOpen(true);
  };

  // Memoize chart colors to avoid re-computation
  const COLORS = useMemo(() => ['hsl(var(--chart-1))', 'hsl(var(--chart-2))', 'hsl(var(--chart-3))', 'hsl(var(--chart-4))', 'hsl(var(--chart-5))'], []);

  // Pay type multipliers - loaded from API or use defaults
  const [payTypeMultipliers, setPayTypeMultipliers] = useState(DEFAULT_MULTIPLIERS);

  // Fetch pay type multipliers on mount
  useEffect(() => {
    const fetchMultipliers = async () => {
      try {
        const response = await axios.get(`${API}/pay-types`);
        if (response.data?.pay_types) {
          const mults = {};
          response.data.pay_types.forEach(pt => {
            if (pt.premium_flag) {
              mults[pt.pay_type] = pt.multiplier;
            }
          });
          setPayTypeMultipliers(prev => ({ ...prev, ...mults }));
        }
      } catch (error) {
        // Non-critical: multiplier fetch failure doesn't block dashboard
        console.error('Failed to load pay type multipliers:', error);
      }
    };
    fetchMultipliers();
  }, [API]);

  // Render KPIs - show skeleton if loading, else actual data
  const renderKPIs = () => {
    if (loading) {
      return (
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
          {[...Array(6)].map((_, i) => <KPISkeleton key={`kpi-skel-${i}`} />)}
        </div>
      );
    }

    const premiumPayPercent = data?.premium_pay_percent || 0;

    return (
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
        {/* Total Pay */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="kpi-card"
          data-testid="kpi-total-pay"
        >
          <div className="flex justify-between items-start mb-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <DollarSign size={20} className="text-blue-600" />
            </div>
          </div>
          <div className="stat-value">{formatCurrency(data?.total_pay || 0)}</div>
          <div className="stat-label mt-1">Total Pay</div>
        </motion.div>

        {/* Premium Pay */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="kpi-card"
          data-testid="kpi-premium-pay"
        >
          <div className="flex justify-between items-start mb-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <DollarSign size={20} className="text-orange-600" />
            </div>
            <span className="text-xs font-bold text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full" title="% of Total Payroll">
              {premiumPayPercent.toFixed(1)}% of payroll
            </span>
          </div>
          <div className="stat-value">{formatCurrency(data?.total_cost || 0)}</div>
          <div className="stat-label mt-1">Premium Pay</div>
        </motion.div>

        {/* Premium Hours */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="kpi-card"
          data-testid="kpi-total-premium"
        >
          <div className="flex justify-between items-start mb-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <Clock size={20} className="text-orange-600" />
            </div>
            <span className="text-xs font-bold text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full" title="% of Total Hours">
              {(data?.premium_percent || 0).toFixed(1)}% of hours
            </span>
          </div>
          <div className="stat-value">{formatHours(data?.total_premium_hours || 0)}</div>
          <div className="stat-label mt-1">Premium Hours</div>
        </motion.div>

        {/* Non-Premium Hours */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="kpi-card"
          data-testid="kpi-total-non-premium"
        >
          <div className="flex justify-between items-start mb-3">
            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
              <Clock size={20} className="text-slate-600" />
            </div>
            <span className="text-xs font-medium text-slate-600 bg-slate-50 px-2 py-0.5 rounded-full">
              {(data?.non_premium_percent || 0).toFixed(1)}%
            </span>
          </div>
          <div className="stat-value">{formatHours(data?.total_non_premium_hours || 0)}</div>
          <div className="stat-label mt-1">Non-Premium Hours</div>
        </motion.div>

        {/* Total Employees */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="kpi-card"
          data-testid="kpi-total-employees"
        >
          <div className="flex justify-between items-start mb-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users size={20} className="text-blue-600" />
            </div>
          </div>
          <div className="stat-value">{data?.total_employees || 0}</div>
          <div className="stat-label mt-1">Employees</div>
        </motion.div>

        {/* Avg Premium/Employee */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="kpi-card"
          data-testid="kpi-avg-premium"
        >
          <div className="flex justify-between items-start mb-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <UserCheck size={20} className="text-orange-600" />
            </div>
          </div>
          <div className="stat-value">{(data?.avg_premium_per_employee || 0).toFixed(1)}h</div>
          <div className="stat-label mt-1">Avg Premium/Emp</div>
        </motion.div>
      </div>
    );
  };

  return (
    <div className="p-6 md:p-8" data-testid="dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#1e3a5f] mb-1">Dashboard</h1>
          <p className="text-muted-foreground">Premium pay analysis and insights</p>
        </div>
      </div>

      {/* KPI Cards */}
      {renderKPIs()}

      {/* Section Navigation - Card Style */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3 mb-6">
        {[
          { id: 'overview', label: 'Overview', icon: LayoutDashboard, color: '#1e3a5f', desc: 'Department breakdown' },
          { id: 'paytype', label: 'By Pay Type', icon: CreditCard, color: '#FF8C42', desc: 'Pay type analysis' },
          { id: 'jobtitle', label: 'By Job Title', icon: Briefcase, color: '#4CAF93', desc: 'Role breakdown' },
          { id: 'trends', label: 'Trends', icon: TrendingDown, color: '#2E5C8A', desc: 'Time patterns' },
          { id: 'supervisors', label: 'Supervisors', icon: UserCheck, color: '#6366f1', desc: 'Manager analysis' },
          { id: 'orgstructure', label: 'Org Structure', icon: Building2, color: '#0ea5e9', desc: 'Hierarchy view' }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`relative p-4 rounded-xl border-2 transition-all text-left ${
              activeTab === tab.id 
                ? 'border-transparent shadow-lg' 
                : 'border-slate-200 hover:border-slate-300 bg-white hover:shadow-md'
            }`}
            style={activeTab === tab.id ? { 
              backgroundColor: tab.color,
              borderColor: tab.color 
            } : {}}
            data-testid={`tab-${tab.id}`}
          >
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${
                activeTab === tab.id 
                  ? 'bg-white/20' 
                  : 'bg-slate-100'
              }`}>
                <tab.icon size={20} className={activeTab === tab.id ? 'text-white' : 'text-slate-600'} style={activeTab !== tab.id ? { color: tab.color } : {}} />
              </div>
              <div>
                <div className={`font-semibold ${activeTab === tab.id ? 'text-white' : 'text-[#1e3a5f]'}`}>
                  {tab.label}
                </div>
                <div className={`text-xs ${activeTab === tab.id ? 'text-white/70' : 'text-slate-500'}`}>
                  {tab.desc}
                </div>
              </div>
            </div>
            {activeTab === tab.id && (
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-0 h-0 border-l-8 border-r-8 border-t-8 border-transparent" style={{ borderTopColor: tab.color }} />
            )}
          </button>
        ))}
      </div>

      {/* Tabbed Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        {/* Hidden TabsList - we use custom buttons above */}
        <TabsList className="hidden">
          <TabsTrigger value="overview" />
          <TabsTrigger value="paytype" />
          <TabsTrigger value="jobtitle" />
          <TabsTrigger value="trends" />
          <TabsTrigger value="supervisors" />
          <TabsTrigger value="orgstructure" />
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-0">
          {loading ? (
            <div className="bento-grid">
              <div className="col-span-12 lg:col-span-8">
                <ChartSkeleton height={350} />
              </div>
              <div className="col-span-12 lg:col-span-4">
                <ChartSkeleton height={350} />
              </div>
              <div className="col-span-12">
                <TableSkeleton />
              </div>
            </div>
          ) : (
            <div className="bento-grid">
              {/* Main Chart - Hours by Department (Stacked: Premium vs Non-Premium) */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="col-span-12 lg:col-span-8 kpi-card"
                data-testid="overtime-chart"
              >
                <h3 className="text-lg font-semibold text-foreground mb-1">Premium Hours by Department</h3>
                <p className="text-sm text-muted-foreground mb-4">Breakdown by pay type - Click to view employees</p>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart 
                    data={data?.records || []}
                    onClick={(e) => {
                      if (e?.activePayload?.[0]?.payload?.dimension) {
                        handleRowClick(e.activePayload[0].payload.dimension);
                      }
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="dimension" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }} />
                    <YAxis tick={{ fill: 'hsl(var(--muted-foreground))' }} tickFormatter={(v) => formatHours(v)} />
                    <Tooltip
                      content={({ active, payload, label }) => {
                        if (!active || !payload || !payload.length) return null;
                        const record = data?.records?.find(r => r.dimension === label);
                        const totalHours = record?.total_hours || 0;
                        const totalPremium = record?.total_premium_hours || 0;
                        const totalNonPremium = record?.non_premium_hours || 0;

                        // Pay-type breakdown is now driven entirely by AppPayType
                        // (server-computed from PremiumFlag). Falls back to empty.
                        const breakdown = record?.premium_breakdown || [];

                        const nonPremiumPct = totalHours > 0 ? ((totalNonPremium / totalHours) * 100).toFixed(1) : 0;
                        const premiumPct = totalHours > 0 ? ((totalPremium / totalHours) * 100).toFixed(1) : 0;

                        return (
                          <div className="bg-white border border-border rounded-lg p-3 shadow-lg min-w-[280px]">
                            {/* Department Name */}
                            <div className="font-semibold text-sm mb-2 pb-2 border-b border-border">
                              {label}
                            </div>

                            {/* Summary: Total, Non-Premium, Premium */}
                            <div className="space-y-1 mb-2 pb-2 border-b border-border">
                              <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">Total Hours:</span>
                                <span className="font-medium">{formatHours(totalHours)} hrs</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">Non-Premium:</span>
                                <span className="font-medium">{formatHours(totalNonPremium)} hrs <span className="text-muted-foreground">({nonPremiumPct}% of total)</span></span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-orange-600 font-medium">Premium:</span>
                                <span className="font-medium text-orange-600">{formatHours(totalPremium)} hrs <span className="text-orange-500">({premiumPct}% of total)</span></span>
                              </div>
                            </div>

                            {/* Pay Type Breakdown */}
                            <div className="text-xs text-muted-foreground mb-1">Premium Breakdown (% of total hours):</div>
                            <div className="space-y-1">
                              {breakdown.map(item => {
                                const pctOfTotal = totalHours > 0 ? ((item.hours / totalHours) * 100).toFixed(1) : 0;
                                if (!item.hours) return null;
                                return (
                                  <div key={item.pay_type} className="flex items-center justify-between gap-4 text-sm">
                                    <div className="flex items-center gap-2">
                                      <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: getPayTypeColor(item.pay_type) }} />
                                      <span>{item.pay_type} ({item.multiplier}x)</span>
                                    </div>
                                    <span className="font-medium">{formatHours(item.hours)} hrs <span className="text-muted-foreground">({pctOfTotal}%)</span></span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      }}
                    />
                    <Legend />
                    {/* Dynamic stacked bars — one per premium pay type from AppPayType.
                        Includes HOLIDAY (or any other pay type the operator promotes). */}
                    {premiumPayTypes.map(pt => (
                      <Bar
                        key={pt.pay_type}
                        dataKey={(record) => {
                          const item = record?.premium_breakdown?.find(b => b.pay_type === pt.pay_type);
                          return item?.hours || 0;
                        }}
                        stackId="premium"
                        fill={getPayTypeColor(pt.pay_type)}
                        name={`${pt.pay_type} (${pt.multiplier}x)`}
                        cursor="pointer"
                        onClick={(d) => d?.dimension && handleRowClick(d.dimension)}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </motion.div>

              {/* Premium Hours by Department - Pie Chart with Cost */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="col-span-12 lg:col-span-4 kpi-card"
                data-testid="cost-chart"
            >
              <h3 className="text-lg font-semibold text-foreground mb-1">Premium Hours Distribution</h3>
              <p className="text-sm text-muted-foreground mb-4">By department with cost - Click to view employees</p>
              <ResponsiveContainer width="100%" height={350}>
                <PieChart>
                  <Pie
                    data={data?.records?.slice(0, 5) || []}
                    dataKey="total_premium_hours"
                    nameKey="dimension"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={(entry) => {
                      const pct = ((entry.total_premium_hours / (data?.total_premium_hours || 1)) * 100).toFixed(1);
                      return `${entry.dimension}: ${pct}%`;
                    }}
                    onClick={(data) => {
                      if (data?.dimension) {
                        handleRowClick(data.dimension);
                      }
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    {(data?.records || []).slice(0, 5).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value, name, props) => {
                      const record = data?.records?.find(r => r.dimension === name);
                      const pct = ((value / (data?.total_premium_hours || 1)) * 100).toFixed(1);
                      const cost = record?.cost || 0;
                      return [`${formatHours(value)} hrs (${pct}%) | Cost: ${formatCurrency(cost)}`, name];
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </motion.div>

            {/* Top Departments Table - Enhanced with Premium % */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="col-span-12 kpi-card"
              data-testid="departments-table"
            >
              <h3 className="text-lg font-semibold text-foreground mb-1">Department Breakdown</h3>
              <p className="text-sm text-muted-foreground mb-4">Premium and non-premium hours by department - Click on a row to view employees. <span className="text-orange-600">Orange = &gt;15% premium</span>, <span className="text-green-600">Green = ≤15% premium</span></p>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        Department
                      </th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        Total Hours
                      </th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        Premium Hrs
                      </th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        Non-Prem Hrs
                      </th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        Premium %
                      </th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        Cost
                      </th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        Employees
                      </th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        Avg Prem/Emp
                      </th>
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.records || []).slice(0, 10).map((record) => (
                      <tr 
                        key={record.dimension} 
                        className="border-b border-border hover:bg-primary/5 transition-colors cursor-pointer group"
                        onClick={() => handleRowClick(record.dimension)}
                        data-testid={`department-row-${record.dimension}`}
                      >
                        <td className="py-3 px-4 font-medium text-foreground">{record.dimension}</td>
                        <td className="py-3 px-4 text-right tabular-nums">{formatHours(record.total_hours || 0)}</td>
                        <td className="py-3 px-4 text-right tabular-nums text-orange-600 font-medium">{formatHours(record.total_premium_hours || record.overtime_hours || 0)}</td>
                        <td className="py-3 px-4 text-right tabular-nums text-slate-500">{formatHours(record.non_premium_hours || 0)}</td>
                        <td className="py-3 px-4 text-right">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            (record.premium_percent || 0) > 20 ? 'bg-red-100 text-red-700' :
                            (record.premium_percent || 0) > 15 ? 'bg-orange-100 text-orange-700' :
                            'bg-green-100 text-green-700'
                          }`}>
                            {(record.premium_percent || 0).toFixed(1)}%
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right tabular-nums">{formatCurrency(record.cost)}</td>
                        <td className="py-3 px-4 text-right tabular-nums">{record.employee_count}</td>
                        <td className="py-3 px-4 text-right tabular-nums">{(record.avg_overtime_per_employee || 0).toFixed(1)}h</td>
                        <td className="py-3 px-4">
                          <ChevronRight size={16} className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
            </div>
          )}
        </TabsContent>

        {/* Pay Type Tab */}
        <TabsContent value="paytype" className="mt-0">
          <PayTypeBreakdown 
            API={API} 
            filters={filters} 
            formatCurrency={formatCurrency}
            formatHours={formatHours}
          />
        </TabsContent>

        {/* Job Title Tab */}
        <TabsContent value="jobtitle" className="mt-0">
          <JobTitleBreakdown 
            API={API} 
            filters={filters}
            formatCurrency={formatCurrency}
            formatHours={formatHours}
            onRowClick={handleRowClick}
          />
        </TabsContent>

        {/* Trends Tab */}
        <TabsContent value="trends" className="mt-0">
          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="kpi-card"
            >
              <OvertimeTrendline onDrillDown={handleDrillDown} />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="kpi-card"
            >
              <SeasonalAnomalies onLocationClick={(location) => {
                handleDrillDown({ type: 'location', value: location });
              }} />
            </motion.div>
          </div>
        </TabsContent>

        {/* Supervisors Tab */}
        <TabsContent value="supervisors" className="mt-0">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="kpi-card"
          >
            <SupervisorAnalysis 
              onDrillDown={handleDrillDown}
            />
          </motion.div>
        </TabsContent>

        {/* Org Structure Tab */}
        <TabsContent value="orgstructure" className="mt-0">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="kpi-card"
          >
            <OrgStructure onDrillDown={handleDrillDown} />
          </motion.div>
        </TabsContent>

      </Tabs>

      {/* Employee Drill-Down Sheet */}
      <EmployeeDrillDown
        isOpen={drillDownOpen}
        onClose={() => setDrillDownOpen(false)}
        filterType={selectedDimension.type}
        filterValue={selectedDimension.value}
        payType={selectedDimension.payType}
        additionalFilters={{
          start_date: filters.start_date,
          end_date: filters.end_date,
          // Use hierarchical context from Org Structure if available, otherwise use global filters
          location: selectedDimension.location || filters.location,
          department: selectedDimension.department || filters.department,
          manager: filters.manager,
          pay_types: filters.pay_types
        }}
      />
    </div>
  );
};

export default Dashboard;
